import { FC, useCallback, useMemo } from 'react';
import { Flex } from 'antd';
import { Modal } from '../../../common/components/modal/Modal';
import { ModalFooter } from '../../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmTargetFormModal.translations.json';
import { RequiredKeyDataType } from '../../schema';
import {
  confirmPanelFormClose,
  filterOption,
  Form,
  FormAlerts,
  FormField,
  FormSelect,
  useAlertContext,
} from '../../../common/form';
import { AlgorithmElementFunctionViewType } from '../../types';
import { Padding } from '../../../common/components/Padding';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { useFormState } from 'react-hook-form';
import { AlgorithmConditionForm } from '../algorithmCondition/AlgorithmConditionForm';
import { createDefaultTargetFormData } from './schema/defaultValues';
import { AlgorithmTargetRuleTypeFields } from './AlgorithmTargetRuleTypeFields';
import { AlgorithmTargetMapTypeFields } from './AlgorithmTargetMapTypeFields';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import {
  AlgorithmTargetFormDataType,
  AlgorithmTargetValidFormDataType,
} from './schema/types';
import { useMutationErrors } from '../../../common/queryClient';
import { confirmationHeaders } from '../../../common/utils';
import validationTranslations from '../../translations/validation.translations.json';
import { useGetAlgorithmSourcesQuery } from '../sources/query/useGetAlgorithmSourcesQuery';
import { ActionButton } from '../../../common/components';
import { AlgorithmTargetSchema } from './schema/AlgorithmTargetSchema';
import { useMutateAlgorithmTargetQuery } from './query/useMutateAlgorithmTargetQuery';
import { useGetAlgorithmMappingsQuery } from '../mappings/query/useGetAlgorithmMappingsQuery';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { getMaxRuleCode, getMaxTargetSeqNr } from './utils';
import { AlgorithmType } from '../schema/zAlgorithmType';

const { addTranslations, t } = i18nNsFactory('algorithm-target-modal');
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export interface AlgorithmTargetsFormModalProps {
  algorithmId: number;
  algorithmTypeCode: AlgorithmType;
  targets: AlgorithmTargetFormDataType[];
  editable?: AlgorithmTargetFormDataType;
  onSuccess?: () => void;
  onClose?: () => void;
  isGraphic: boolean;
  onIsGraphicChange: (isGraphic: boolean) => void;
}

export const AlgorithmTargetFormModal: FC<AlgorithmTargetsFormModalProps> = (
  props,
) => {
  const {
    algorithmId,
    algorithmTypeCode,
    targets,
    editable,
    onSuccess,
    onClose,
    isGraphic,
    onIsGraphicChange,
  } = props;
  const { data: algorithmSources } = useGetAlgorithmSourcesQuery({
    id: algorithmId,
  });
  const { data: algorithmMappings } = useGetAlgorithmMappingsQuery({
    id: algorithmId,
  });

  const { mutateAsync } = useMutateAlgorithmTargetQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  const target = useMemo(() => {
    let maxTargetSeqNr = getMaxTargetSeqNr(targets);
    return (
      editable ?? createDefaultTargetFormData(algorithmId, ++maxTargetSeqNr)
    );
  }, [editable, algorithmId, targets]);

  const handleSubmit = useCallback(
    (values: AlgorithmTargetValidFormDataType, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: target.id === -1 ? undefined : target.id,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(() => {
          onSuccess?.();
          setSuccess();
        })
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      target.id,
      onSuccess,
      setSuccess,
      setMutationErrors,
    ],
  );

  if (!algorithmSources || !algorithmMappings) {
    return null;
  }

  return (
    <Form
      schema={AlgorithmTargetSchema.validFormDataSchema}
      defaultValues={target}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="algorithmTargets"
    >
      <AlgorithmTargetFormModalInner
        algorithmTypeCode={algorithmTypeCode}
        targets={targets}
        sources={algorithmSources.sources}
        mappings={algorithmMappings.mappings}
        onClose={onClose}
        isGraphic={isGraphic}
        onIsGraphicChange={onIsGraphicChange}
      />
    </Form>
  );
};

function AlgorithmTargetFormModalInner({
  algorithmTypeCode,
  targets,
  sources,
  mappings,
  onClose,
  isGraphic,
  onIsGraphicChange,
}: {
  algorithmTypeCode: AlgorithmType;
  targets?: AlgorithmTargetFormDataType[];
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
  onClose?: () => void;
  isGraphic: boolean;
  onIsGraphicChange: (isGraphic: boolean) => void;
}) {
  const { isDirty } = useFormState();
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const requiredKeyOptions = useMemo(
    () =>
      Array.from(
        (dataObjectDescriptionVersion.requiredKeys || [])
          .reduce((uniq, curr) => {
            if (!uniq.has(curr['key'])) {
              uniq.set(curr['key'], curr);
            }
            return uniq;
          }, new Map())
          .values(),
      ).map((requiredKey: RequiredKeyDataType) => ({
        value: requiredKey.key,
        label: `${requiredKey.sequenceNumber} (${requiredKey.key})`,
      })),
    [dataObjectDescriptionVersion],
  );

  const maxRuleCode = useMemo(
    () => getMaxRuleCode((targets || []).flatMap((target) => target.rules)),
    [targets],
  );

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="60rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`title`)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="large">
          <AlgorithmConditionForm
            name="condition"
            label={t('condition.label')}
            rows={1}
            viewType={AlgorithmElementFunctionViewType.T}
            sources={sources}
            mappings={mappings}
          />
          {!!requiredKeyOptions.length &&
            algorithmTypeCode === AlgorithmType.INSERT && (
              <FormField label={t('targetKey.label')} name={`targetKey`}>
                <FormSelect
                  name={`targetKey`}
                  placeholder={t('targetKey.placeholder')}
                  options={requiredKeyOptions}
                  filterOption={filterOption}
                  showSearch
                  allowClear
                />
              </FormField>
            )}
          <FormField name="description" label={t('description.label')} />
          <AlgorithmTargetMapTypeFields
            algorithmTypeCode={algorithmTypeCode}
            maxRuleCode={maxRuleCode}
            sources={sources}
            mappings={mappings}
            isGraphic={isGraphic}
            onIsGraphicChange={onIsGraphicChange}
          />
          <AlgorithmTargetRuleTypeFields
            algorithmTypeCode={algorithmTypeCode}
            maxRuleCode={maxRuleCode}
            sources={sources}
            mappings={mappings}
          />
        </Flex>
      </Padding>
    </Modal>
  );
}
